import {
  type OutletResource,
  OutletsController_list_v1,
  type TerminalResource,
  TerminalsController_list_v1,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconCircleCheck, IconPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';

import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { isRetired } from '../outlets/outlet-kinds';
import { NewTerminalDialog } from './NewTerminalDialog';
import { RegisterTerminalDialog } from './RegisterTerminalDialog';
import { RevokeTerminalDialog } from './RevokeTerminalDialog';
import { labelForState, stateOf, type TerminalState } from './terminal-states';

/**
 * The devices the park takes money on — and the rows that are not devices yet.
 *
 * **Half of this screen is a distinction.** A terminal created here is a record:
 * an outlet, a prefix and a name. It becomes a device when somebody stands at
 * the machine and registers it (FE011), and until then it can issue nothing. The
 * status column exists to say which of the two a row is, because a list that
 * implied every row was a live till would send somebody to a gate with a prefix
 * that cannot open one.
 *
 * **A prefix is taken for all time.** It is printed on tickets and receipts that
 * outlive the device, so the API never releases one — not even after revocation
 * — and this screen says so in the form, before the 409 has to. Compare outlet
 * codes, which *are* released on retirement: the two rules look alike and are
 * opposites, and the copy in each place is written to be read alone.
 *
 * **Revoked terminals stay on the list.** The API returns them with their status
 * rather than deleting them, for the same reason the prefix is never reused: the
 * receipts are still out there and somebody will come here holding one.
 *
 * Two reads, not one. The terminals carry `outletId` and nothing else about
 * where they stand, so the outlets are read alongside them — for the column, for
 * the filter, and for the create form's picker, which is the one place the
 * retired ones are kept out.
 */

/** Every state a load can be in. One value, so two of them cannot coexist. */
type LoadState<T> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly rows: readonly T[] }
  | { readonly kind: 'failed'; readonly error: unknown };

/** The filter's "no filter". A sentinel, because `Select` has no empty value. */
const EVERY_OUTLET = 'ALL';

/** Kept next to the header cells below, so the two cannot drift. */
const COLUMNS = 5;

/** One instance, so "not loaded yet" is a stable value and not a new array. */
const NO_OUTLETS: readonly OutletResource[] = [];

export function Terminals() {
  const client = useApiClient();

  const [outletFilter, setOutletFilter] = useState<string>(EVERY_OUTLET);
  const [outlets, setOutlets] = useState<LoadState<OutletResource>>({ kind: 'loading' });
  const [terminals, setTerminals] = useState<LoadState<TerminalResource>>({ kind: 'loading' });
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState<TerminalResource | null>(null);

  // The row a dialog is acting on, rather than a boolean plus an id: two pieces
  // of state can disagree about which terminal is being revoked, and one cannot.
  const [registering, setRegistering] = useState<TerminalResource | null>(null);
  const [revoking, setRevoking] = useState<TerminalResource | null>(null);

  // Bumping this re-runs both loads. A counter rather than a boolean, so two
  // retries in a row are two distinct values and the second one still fires.
  const [reloads, setReloads] = useState(0);

  const reload = useCallback(() => setReloads((count) => count + 1), []);

  useEffect(() => {
    // Two guards, not one, for the reason `Outlets.tsx` gives: `controller`
    // stops the request and `live` stops a response already in flight from
    // landing in state after this effect was torn down.
    const controller = new AbortController();
    let live = true;

    setOutlets({ kind: 'loading' });

    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        // Retired ones included, deliberately. A terminal that stood in an
        // outlet since retired is still on this list and still has to name
        // where it stood; leaving them out would render that row against a
        // blank. Creating against a retired outlet is the thing that is
        // refused, and that is the create form's picker, not this read.
        query: { offset, limit, includeRetired: true },
        signal: controller.signal,
      }),
    )
      .then((rows) => {
        if (live) setOutlets({ kind: 'loaded', rows });
      })
      .catch((error: unknown) => {
        if (live) setOutlets({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, reloads]);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    setTerminals({ kind: 'loading' });

    collectPages<TerminalResource>((offset, limit) =>
      client.request(TerminalsController_list_v1, {
        // Filtered at the API rather than in the browser. The list is read
        // whole per outlet, so a park with more terminals than one page still
        // filters correctly — a client-side filter over one page would quietly
        // hide the rest.
        query: {
          offset,
          limit,
          ...(outletFilter === EVERY_OUTLET ? {} : { outletId: outletFilter }),
        },
        signal: controller.signal,
      }),
    )
      .then((rows) => {
        if (live) setTerminals({ kind: 'loaded', rows });
      })
      .catch((error: unknown) => {
        if (live) setTerminals({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, outletFilter, reloads]);

  // Memoised rather than a bare ternary: the fallback would be a new empty
  // array on every render, which re-runs both derivations below for nothing.
  const outletRows = useMemo(
    () => (outlets.kind === 'loaded' ? outlets.rows : NO_OUTLETS),
    [outlets],
  );

  // Named rather than looked up down a list in every cell. A `Map` also makes
  // the miss explicit below instead of rendering `undefined`.
  const outletNames = useMemo(
    () => new Map(outletRows.map((outlet) => [outlet.id, outlet.name])),
    [outletRows],
  );

  // The filter's own value-to-label map. Separate from `outletNames` because it
  // carries the "all outlets" row too, which is not an outlet.
  const outletFilterItems = useMemo(
    () => [
      { label: 'All outlets', value: EVERY_OUTLET },
      ...outletRows.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    ],
    [outletRows],
  );

  // The picker's outlets and the filter's are deliberately different sets. This
  // one is where a retired outlet is refused — before a form is filled in
  // rather than after it is submitted.
  const openOutlets = useMemo(
    () => outletRows.filter((outlet) => !isRetired(outlet)),
    [outletRows],
  );

  // Either read failing fails the screen. A table of terminals against blank
  // outlet cells is worse than saying the list could not be read: it looks like
  // terminals that belong nowhere, which is a state this system does not have.
  const failure =
    terminals.kind === 'failed' ? terminals : outlets.kind === 'failed' ? outlets : null;

  const loading = failure === null && (terminals.kind === 'loading' || outlets.kind === 'loading');

  return (
    <Screen
      title="Terminals"
      description="Every till and gate device the park takes money on. A terminal is created here and registered on the device itself, so a new row is a place reserved for a machine rather than a machine that is running."
      action={
        // The screen's one primary action, and the only accent on it. Rows
        // carry no buttons of their own for the same reason.
        <Button onClick={() => setCreating(true)}>
          <IconPlus />
          New terminal
        </Button>
      }
      aside={
        <>
          {terminals.kind === 'loaded' && (
            <ScreenFact label="Showing">
              <span className="font-mono numeric">{terminals.rows.length}</span>{' '}
              {terminals.rows.length === 1 ? 'terminal' : 'terminals'}
            </ScreenFact>
          )}

          <div className="space-y-2">
            <Label id="outlet-filter-label" htmlFor="outlet-filter" className="text-sm">
              Outlet
            </Label>
            <Select
              value={outletFilter}
              // Base UI types the value as nullable because a select may be
              // cleared. This one cannot be — there is always a choice, and "no
              // outlet chosen" is spelled `EVERY_OUTLET` rather than as an
              // absence, so the filter can never fall into a state with no label.
              onValueChange={(next) => setOutletFilter(next ?? EVERY_OUTLET)}
              // The trigger's label comes from here rather than from the items
              // below it, so an outlet id would show through without it.
              items={outletFilterItems}
              disabled={outlets.kind !== 'loaded'}
            >
              <SelectTrigger id="outlet-filter" aria-label="Outlet" className="h-9 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {/* The group carries the popup's padding. */}
                <SelectGroup>
                  <SelectItem value={EVERY_OUTLET}>All outlets</SelectItem>
                  {outletRows.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id}>
                      {outlet.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </>
      }
    >
      {justCreated !== null && (
        <Alert
          // `status` and not the component's default `alert`: a success is not
          // an interruption, and this is announced politely rather than cutting
          // across whatever the reader was doing.
          role="status"
        >
          <IconCircleCheck />
          <AlertDescription>
            <span className="font-mono text-foreground numeric">{justCreated.prefix}</span> —{' '}
            {justCreated.name} has been created. Register it on the device itself to put it into
            service.
          </AlertDescription>
        </Alert>
      )}

      <ScreenTable label="Terminals">
        <TableHeader>
          <TableRow>
            <TableHead className="w-32">Prefix</TableHead>
            <TableHead>Name</TableHead>
            <TableHead className="w-56">Outlet</TableHead>
            <TableHead className="w-48">Status</TableHead>
            {/* Named for a screen reader and not for the eye: the buttons in
                this column already say what they do and to which terminal, and
                a visible "Actions" heading would be a fifth label saying less
                than any of them. */}
            <TableHead className="w-64 text-right">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>

        {loading && <TableSkeleton columns={COLUMNS} />}

        {failure !== null && (
          <TableNotice columns={COLUMNS}>
            <LoadFailed error={failure.error} onRetry={reload} />
          </TableNotice>
        )}

        {failure === null &&
          !loading &&
          terminals.kind === 'loaded' &&
          (terminals.rows.length === 0 ? (
            <TableNotice columns={COLUMNS}>
              <EmptyState
                title="No terminals yet"
                description={
                  outletFilter === EVERY_OUTLET
                    ? 'A terminal is one till or gate device, standing in one outlet. New terminal creates the first one; it is registered on the device itself afterwards.'
                    : 'This outlet has no terminals. New terminal creates one for it, or choose All outlets to see the rest of the park.'
                }
              />
            </TableNotice>
          ) : (
            <TableBody>
              {terminals.rows.map((terminal) => (
                <TerminalRow
                  key={terminal.id}
                  terminal={terminal}
                  outletName={outletNames.get(terminal.outletId)}
                  onRegister={() => setRegistering(terminal)}
                  onRevoke={() => setRevoking(terminal)}
                />
              ))}
            </TableBody>
          ))}
      </ScreenTable>

      <NewTerminalDialog
        open={creating}
        onOpenChange={setCreating}
        outlets={openOutlets}
        // The outlet being looked at is the one being added to, nine times in
        // ten. Not forced: the picker still offers every open outlet.
        defaultOutletId={outletFilter === EVERY_OUTLET ? null : outletFilter}
        onCreated={(terminal) => {
          setCreating(false);
          setJustCreated(terminal);

          // Re-read rather than splicing the new terminal into the list. The
          // API decides the order and whether anything else moved while this
          // form was open, and a locally patched list is a second answer to a
          // question the server has already answered.
          reload();
        }}
      />

      <RegisterTerminalDialog
        terminal={registering}
        outletNames={outletNames}
        onClose={() => setRegistering(null)}
        // Re-read rather than patching the row: registration changes the
        // terminal's status, and the API is the one that says what it now is.
        // The dialog stays open behind this, holding the lease.
        onRegistered={reload}
      />

      <RevokeTerminalDialog
        terminal={revoking}
        onClose={() => setRevoking(null)}
        onRevoked={() => {
          setRevoking(null);
          reload();
        }}
      />
    </Screen>
  );
}

function TerminalRow({
  terminal,
  outletName,
  onRegister,
  onRevoke,
}: {
  terminal: TerminalResource;
  outletName: string | undefined;
  onRegister: () => void;
  onRevoke: () => void;
}) {
  const state = stateOf(terminal);

  return (
    // A revoked row is quieter, not struck through or greyed past reading: it is
    // history that must stay legible, because the reason it is still here is
    // that somebody is looking something up. `text-muted-foreground` is safe —
    // the row sits on `bg-card`, and it is only muted-on-muted that misses AA.
    <TableRow className={state === 'REVOKED' ? 'text-muted-foreground' : undefined}>
      {/* A prefix is read off a receipt and typed back in, so it gets the mono
          face and tabular figures — the column has to compare cleanly down its
          length. */}
      <TableCell className="font-mono text-foreground numeric">{terminal.prefix}</TableCell>
      <TableCell className={state === 'REVOKED' ? undefined : 'text-foreground'}>
        {terminal.name}
      </TableCell>
      <TableCell>
        {/* A terminal always has an outlet; this arm means the outlet read and
            the terminal read disagreed, which is a moment in time and not a
            state. Saying so beats printing a UUID nobody can act on. */}
        {outletName ?? <span className="text-muted-foreground">Unknown outlet</span>}
      </TableCell>
      <TableCell>
        <TerminalStatus state={state} />
      </TableCell>
      <TableCell className="text-right">
        <TerminalActions
          terminal={terminal}
          state={state}
          onRegister={onRegister}
          onRevoke={onRevoke}
        />
      </TableCell>
    </TableRow>
  );
}

/**
 * What can be done to one row, said in the row.
 *
 * Neither button is `primary`. The screen has one accent and it is on "New
 * terminal" — a table with a green button on every row has no primary action,
 * it has twenty equal ones, and the one that withdraws a till from service is
 * not the one to make the easiest to hit while moving fast.
 *
 * Every name carries the prefix. Twenty rows of "Revoke" are twenty buttons a
 * screen reader announces identically, and the prefix is what the person
 * confirming is actually holding — it is on the receipt in their hand.
 */
function TerminalActions({
  terminal,
  state,
  onRegister,
  onRevoke,
}: {
  terminal: TerminalResource;
  state: TerminalState;
  onRegister: () => void;
  onRevoke: () => void;
}) {
  // Nothing is done to a withdrawn terminal. It is not re-registered here — a
  // device that comes back is a new terminal under a new prefix — and revoking
  // it again is a control with nothing behind it.
  if (state === 'REVOKED') return null;

  return (
    <div className="flex justify-end gap-2">
      <Button
        variant="outline"
        size="sm"
        aria-label={`Revoke ${terminal.prefix}`}
        onClick={onRevoke}
      >
        Revoke
      </Button>
      <Button
        variant="secondary"
        size="sm"
        // "Re-register" rather than the same word twice: pressing it on a live
        // till supersedes the lease that till is using, and the button should
        // not read as though nothing had happened yet.
        aria-label={`${state === 'IN_SERVICE' ? 'Re-register' : 'Register'} ${terminal.prefix}`}
        onClick={onRegister}
      >
        {state === 'IN_SERVICE' ? 'Re-register' : 'Register'}
      </Button>
    </div>
  );
}

function TerminalStatus({ state }: { state: TerminalState }) {
  const label = labelForState(state);

  // In service is the ordinary case, and the ordinary case is not a badge. Plain
  // text here is what lets the two badges below carry meaning: a column where
  // every cell is a pill is a column with no signal in it.
  if (state === 'IN_SERVICE') return <span className="text-muted-foreground">{label}</span>;

  // `secondary` and not `destructive` for the waiting row: nothing is wrong
  // with it. It is a step that has not been taken, and red would read as a
  // fault to fix rather than as work to finish.
  //
  // `outline` for revoked, for the reason `Outlets.tsx` gives about retirement:
  // red in this system means the system refused something or is about to, and a
  // withdrawal that already happened is a fact rather than a refusal.
  return <Badge variant={state === 'REVOKED' ? 'outline' : 'secondary'}>{label}</Badge>;
}
